// Slides personnalisées avec les prénoms des participants.
// Injectées dynamiquement en tête et en fin des RVP1 / RVP2 / AM lorsque
// des participants sont renseignés dans le modal d'accueil.

import React from 'react';
import { Sparkles, Heart, Users, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

/* ────────────── Slide : Bienvenue personnalisée ────────────── */
export function SlideBienvenue({ slide, joinUrl }) {
  const eleves = slide.eleves || [];
  const allParents = eleves.flatMap((e) => (e.parents || []).map((p) => ({ ...p, eleveId: e.id })));

  return (
    <div className="h-full flex flex-col justify-center items-center text-center px-4 sm:px-16 py-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
        <Sparkles size={12} className="text-red-400" />
        <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Bienvenue</span>
      </div>
      <h1 className="font-serif-editorial text-white text-[32px] sm:text-[54px] leading-[1.05] tracking-tight max-w-4xl">
        {slide.title || 'Bienvenue à toutes et à tous'}
      </h1>
      {slide.subtitle && <p className="mt-4 text-white/70 text-[15px] sm:text-[19px] max-w-2xl">{slide.subtitle}</p>}

      {/* Élèves — grande carte par prénom */}
      <div className={`mt-8 sm:mt-10 grid gap-3 sm:gap-4 w-full max-w-4xl ${
        eleves.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' :
        eleves.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' :
        'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
      }`}>
        {eleves.map((e, i) => (
          <div
            key={e.id || i}
            className="rounded-2xl border border-emerald-500/40 bg-emerald-950/20 p-4 sm:p-5"
            style={{ animationDelay: `${i * 90}ms` }}
          >
            <div className="text-[36px] sm:text-[48px] leading-none">{e.emoji || '⭐'}</div>
            <div className="mt-2 font-serif-editorial text-white text-[18px] sm:text-[24px] leading-tight break-words">
              {e.prenom}
            </div>
            {e.nom && <div className="mt-0.5 text-white/50 text-[11.5px] font-mono-spec uppercase tracking-wider">{e.nom}</div>}
          </div>
        ))}
      </div>

      {/* Parents (bandeau plus discret en dessous) */}
      {allParents.length > 0 && (
        <div className="mt-6 sm:mt-8 pt-5 border-t border-white/10 w-full max-w-3xl">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-300 font-bold font-mono-spec mb-3 inline-flex items-center gap-1.5">
            <Users size={11} /> Accompagné·e·s de
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {allParents.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-500/25 bg-sky-950/20 text-white/85 text-[12.5px] sm:text-[14px]">
                <span className="text-sky-300 text-[10.5px] font-mono-spec">{p.lien}</span>
                <span className="font-semibold">{p.prenom}{p.nom ? ` ${p.nom}` : ''}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Inscription QR facultative pendant la présentation du déroulé */}
      {joinUrl && (
        <div className="mt-6 sm:mt-8 flex items-center gap-3 sm:gap-4 rounded-xl border border-emerald-500/30 bg-emerald-950/15 px-4 py-3" data-testid="bienvenue-join-qr">
          <div className="bg-white p-1.5 rounded-lg shrink-0"><QRCodeSVG value={joinUrl} size={84} level="M" /></div>
          <div className="text-left">
            <div className="text-emerald-300 font-mono-spec text-[10px] sm:text-[11.5px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
              <Smartphone size={11} /> Facultatif · rejoignez avec votre téléphone
            </div>
            <p className="text-white/70 text-[11.5px] sm:text-[13.5px] mt-1 leading-snug">Scannez pour indiquer votre prénom, suivre la séance et garder le rappel du cours.</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────── Slide : Merci personnalisé ────────────── */
export function SlideMerci({ slide }) {
  const eleves = slide.eleves || [];
  const prenoms = eleves.map((e) => e.prenom).filter(Boolean);
  const joinedPrenoms = prenoms.length === 0 ? '' :
    prenoms.length === 1 ? prenoms[0] :
    prenoms.slice(0, -1).join(', ') + ' et ' + prenoms[prenoms.length - 1];

  return (
    <div className="h-full flex flex-col justify-center items-center text-center px-4 sm:px-16 py-10">
      <Heart size={40} className="text-red-400 mb-4" />
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-3">Merci</div>
      <h1 className="font-serif-editorial text-white text-[36px] sm:text-[58px] leading-[1.05] tracking-tight max-w-4xl">
        {slide.title || (prenoms.length > 0 ? `Merci ${joinedPrenoms}` : 'Merci pour votre attention')}
      </h1>
      {slide.subtitle && <p className="mt-5 text-white/70 text-[15px] sm:text-[19px] max-w-2xl leading-relaxed">{slide.subtitle}</p>}

      {/* Récap participants — petite ligne */}
      {eleves.length > 0 && (
        <div className="mt-8 sm:mt-10 flex flex-wrap gap-2 justify-center max-w-3xl">
          {eleves.map((e, i) => (
            <span key={e.id || i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-950/15 text-white/90 text-[13px]">
              <span className="text-[16px]">{e.emoji || '⭐'}</span>
              <span className="font-semibold">{e.prenom}</span>
            </span>
          ))}
        </div>
      )}

      {slide.next_action && (
        <div className="mt-10 inline-flex items-center gap-2 px-5 py-3 rounded-full border border-red-500/40 bg-red-950/30 text-red-200 text-[13px] sm:text-[15px] font-semibold font-mono-spec uppercase tracking-wider">
          <Sparkles size={14} /> {slide.next_action}
        </div>
      )}
    </div>
  );
}
